import lawyer from './assistant-lawyer.webp';
import lawyerArm from './assistant-lawyer-arm.webp';
import lawyerBlink from './assistant-lawyer-blink.webp';
import lawyerBody from './assistant-lawyer-body.webp';
import lawyerClean from './assistant-lawyer-clean.webp';
import lawyerFace from './assistant-lawyer-face.webp';
import lawyerFolder from './assistant-lawyer-folder.webp';
import lawyerHead from './assistant-lawyer-head.webp';
import lawyerLegLeft from './assistant-lawyer-legl.webp';
import lawyerLegRight from './assistant-lawyer-legr.webp';

/**
 * The assistant's character: the young lawyer in a black suit the product owner chose.
 *
 * In the corner he is a cut-out puppet made from that same picture: layers on one
 * 680 x 1208 canvas (the picture is 454 wide; the rest is room for the folder held out).
 *
 * - Walking: he faces us, so he walks towards us. Animated the way a walk cycle is drawn:
 *   contact and passing poses with eased timing; the body rises over the standing leg
 *   and tips onto it while the other leg lifts; the standing leg stretches so that foot
 *   stays planted rather than sliding; the head lags the body a beat; a soft shadow under
 *   his feet tightens as he rises. He slows as he arrives, settles with a small dip and a
 *   nod, then breathes, blinks and glances about while he waits. A bubble keeps pace
 *   ("Need updates?") and changes to "Click to find out!" when he stops. Plays once.
 * - When the corner peeks (`.m-peek` on an ancestor: hover, focus or a tap): his forearm swings out and he
 *   holds the folder away from his body, towards us. For that pose the body layer is
 *   swapped for one with the folder and forearm painted out, and a drawn forearm joins
 *   his elbow to the hand on the folder.
 *
 * `MascotPortrait` and `MascotFace` are the plain picture and his face, for the panel.
 * Motion is skipped for anyone who asks for less.
 */

/** Five steps of half a second each. */
export const WALK_SECONDS = 2.5;
/** How long the stopped bubble stays before it waits for a hover. */
export const BUBBLE_SECONDS = 10;

// Pivots below are shares of the 680 x 1208 canvas: each hip, the neck, the hand on the
// folder. Holding it out: the hand moves from (268, 530) to (480, 482), the folder nearer.
const HELD_OUT = 'translate(31.18%, -4.14%) rotate(-4deg) scale(1.1)';
const SPRING = 'cubic-bezier(.3, 1.4, .5, 1)';
/** Arriving: quick at first, easing off as he gets close. Shared so the bubble keeps up. */
const ARRIVE = 'cubic-bezier(.22, .45, .35, 1)';
const SETTLE_AT = WALK_SECONDS;
const IDLE_AT = WALK_SECONDS + 0.6;

const CSS = `
.m-puppet { position: relative; display: block; aspect-ratio: 680 / 1208; transform-origin: 50% 100%; }
.m-fig, .m-head, .m-fig img { position: absolute; inset: 0; width: 100%; height: 100%; }
.m-fig { transform-origin: 33% 100%; }
.m-leg-l { transform-origin: 22% 58%; }
.m-leg-r { transform-origin: 39% 58%; }
.m-head { transform-origin: 30.9% 21.7%; }
.m-folder { transform-origin: 39.41% 43.87%; }
.m-shadow {
  position: absolute; left: 4%; width: 58%; bottom: -1.4%; height: 3.4%; border-radius: 50%;
  background: radial-gradient(closest-side, rgba(20, 16, 12, .34), rgba(20, 16, 12, 0));
}

/* At rest: the picture as drawn. Held out: the painted-out body, the drawn forearm, the
   folder moved. Going back, the folder returns first and the swap happens after it. */
.m-clean, .m-arm, .m-folder { opacity: 0; }
.m-folder { transition: transform .3s ease-in, opacity 0s .3s; }
.m-clean { transition: opacity 0s .3s; }
.m-body { transition: opacity 0s .3s; }
.m-arm { transition: opacity .08s; }
.m-peek .m-folder {
  opacity: 1; transform: ${HELD_OUT}; transition: transform .45s ${SPRING}, opacity 0s;
}
.m-peek .m-clean { opacity: 1; transition: opacity 0s; }
.m-peek .m-body { opacity: 0; transition: opacity 0s; }
.m-peek .m-arm { opacity: 1; transition: opacity .12s .22s; }

/* Alive while he waits: breathing, a blink now and then, a glance about. */
.m-blink { opacity: 0; animation: m-blink 5.3s 1.4s infinite; }
.m-stopped .m-fig { animation: m-breathe 3.8s ease-in-out infinite; }
.m-stopped .m-head { animation: m-look 7.5s ease-in-out infinite; }

/* The walk towards us: five steps, then a settle, then the waiting loop. */
.m-walking { animation: m-come ${WALK_SECONDS}s ${ARRIVE} both; }
.m-walking .m-puppet { animation: m-grow ${WALK_SECONDS}s ${ARRIVE} both; }
.m-walking .m-fig {
  animation: m-stride 1s 0s 2.5, m-settle .6s ease-out ${SETTLE_AT}s,
             m-breathe 3.8s ease-in-out ${IDLE_AT}s infinite;
}
.m-walking .m-leg-l { animation: m-leg-l 1s 0s 2.5; }
.m-walking .m-leg-r { animation: m-leg-r 1s 0s 2.5; }
.m-walking .m-head {
  animation: m-head-walk 1s 0s 2.5, m-nod .8s ease-in-out ${SETTLE_AT + 0.05}s,
             m-look 7.5s ease-in-out ${IDLE_AT + 0.4}s infinite;
}
.m-walking .m-shadow { animation: m-shadow .5s ease-in-out 0s 5; }
.m-bubble-follow {
  animation: m-follow ${WALK_SECONDS}s ${ARRIVE} both, m-bubble-bob .5s ease-in-out 0s 5;
}

@keyframes m-come { from { transform: translate(-70px, -40px); } to { transform: none; } }
@keyframes m-grow { from { transform: scale(.55); } to { transform: none; } }
/* The bubble is outside the growing figure so its text stays readable; this keeps it
   beside his head while he grows (figure box 99 px wide, his head about 140 px up). */
@keyframes m-follow { from { transform: translate(22px, 63px); } to { transform: none; } }
@keyframes m-bubble-bob { 0%, 100% { translate: 0 0; } 50% { translate: 0 -3px; } }

/* One stride is two steps. 0% and 50% are contact (both feet down, body low); 25% and
   75% are passing (one leg lifted, the body high over the other and tipped onto it). */
@keyframes m-stride {
  0%, 50%, 100% { transform: translateY(.8%) rotate(0); animation-timing-function: cubic-bezier(.33, 0, .3, 1); }
  25% { transform: translateY(-1.2%) rotate(1.5deg); animation-timing-function: cubic-bezier(.6, 0, .67, 1); }
  75% { transform: translateY(-1.2%) rotate(-1.5deg); animation-timing-function: cubic-bezier(.6, 0, .67, 1); }
}
/* Lifted: shorter (the knee comes towards us), the foot tucks in. Standing: stretched by
   what the body rose, so its foot stays on the ground. Contact: both give a little. */
@keyframes m-leg-l {
  0%, 50%, 100% { transform: scaleY(.985); animation-timing-function: cubic-bezier(.33, 0, .3, 1); }
  25% { transform: translateX(1.2%) rotate(-2.5deg) scale(1.03, .87); animation-timing-function: cubic-bezier(.6, 0, .67, 1); }
  75% { transform: scaleY(1.03); animation-timing-function: cubic-bezier(.6, 0, .67, 1); }
}
@keyframes m-leg-r {
  0%, 50%, 100% { transform: scaleY(.985); animation-timing-function: cubic-bezier(.33, 0, .3, 1); }
  25% { transform: scaleY(1.03); animation-timing-function: cubic-bezier(.6, 0, .67, 1); }
  75% { transform: translateX(-1.2%) rotate(2.5deg) scale(1.03, .87); animation-timing-function: cubic-bezier(.6, 0, .67, 1); }
}
/* The head follows the body a beat late, so it does not move as one stiff block. */
@keyframes m-head-walk {
  0%, 55%, 100% { transform: none; } 30% { transform: rotate(-2.2deg); } 80% { transform: rotate(2.2deg); }
}
@keyframes m-shadow { 0%, 100% { transform: scaleX(1); opacity: 1; } 50% { transform: scaleX(.86); opacity: .7; } }
@keyframes m-settle {
  0% { transform: translateY(.8%); } 35% { transform: translateY(1.6%) scale(1.01, .985); }
  70% { transform: translateY(-.4%); } 100% { transform: none; }
}
@keyframes m-nod { 0%, 100% { transform: none; } 40% { transform: rotate(4deg) translateY(.6%); } }
@keyframes m-breathe { 0%, 100% { transform: none; } 50% { transform: scale(1.004, 1.009); } }
@keyframes m-look {
  0%, 30%, 100% { transform: none; } 40%, 52% { transform: rotate(-2deg); } 64%, 76% { transform: rotate(1.6deg); }
}
@keyframes m-blink { 0%, 93%, 98%, 100% { opacity: 0; } 94%, 97% { opacity: 1; } }


/* The bubble: pops in; typing dots while he "thinks". Its wrapper and the card's ignore
   the pointer, so hidden boxes neither block clicks nor open the card. */
.m-bubble { transform-origin: 100% 100%; transition: opacity .2s, scale .2s; }
.m-bubble[data-shown='false'] { opacity: 0; scale: .85; pointer-events: none; visibility: hidden;
  transition: opacity .15s, scale .15s, visibility 0s .15s; }
.m-bubble[data-shown='true'] { pointer-events: auto; animation: m-pop-in .32s ${SPRING} both; }
@keyframes m-pop-in { from { opacity: 0; scale: .7; } to { opacity: 1; scale: 1; } }
.m-dot { display: inline-block; width: 6px; height: 6px; border-radius: 50%; background: currentColor;
  opacity: .45; animation: m-dot 1s ease-in-out infinite; }
.m-dot:nth-child(2) { animation-delay: .15s; } .m-dot:nth-child(3) { animation-delay: .3s; }
@keyframes m-dot { 0%, 60%, 100% { transform: none; opacity: .45; } 30% { transform: translateY(-3px); opacity: 1; } }

/* The update card: a sheet that slides out of his folder towards the left and settles. */
.m-card { transform-origin: 100% 100%;
  transition: transform .42s ${SPRING}, opacity .2s, visibility 0s; }
.m-card[data-shown='false'] { opacity: 0; visibility: hidden; pointer-events: none;
  transform: translate(70px, 18px) rotate(9deg) scale(.35);
  transition: transform .22s ease-in, opacity .18s .04s, visibility 0s .22s; }
.m-card[data-shown='true'] { pointer-events: auto; transform: rotate(-1.2deg); }

/* Someone is typing in a form: he holds still and keeps quiet. */
.m-hush, .m-hush * { animation-play-state: paused !important; }
.m-busy { animation: m-nod-avatar 1.2s ease-in-out infinite; transform-origin: 50% 80%; }
@keyframes m-nod-avatar {
  0%, 100% { transform: rotate(0) translateY(0); } 30% { transform: rotate(-6deg) translateY(-1px); }
  60% { transform: rotate(5deg) translateY(0); }
}

@media (prefers-reduced-motion: reduce) {
  .m-walking, .m-walking *, .m-stopped *, .m-busy, .m-blink, .m-bubble-follow, .m-bubble, .m-dot { animation: none !important; }
  .m-card { transition: none !important; }
  .m-folder, .m-arm, .m-clean, .m-body { transition: none !important; }
}
`;

/** The keyframes, once per page. Rendered by whichever mascot mounts. */
export function MascotStyles() {
  return <style>{CSS}</style>;
}

/** The corner figure. `walking`: play the walk in; otherwise he stands and idles. */
export function CornerMascot({ walking, className }: { walking: boolean; className?: string }) {
  return (
    <span className={[walking ? 'm-walking' : 'm-stopped', 'relative block', className ?? ''].join(' ')}>
      <span aria-hidden="true" className="m-puppet h-44">
        <span className="m-shadow" />
        <span className="m-fig">
          <img src={lawyerLegLeft} alt="" draggable={false} className="m-leg-l select-none" />
          <img src={lawyerLegRight} alt="" draggable={false} className="m-leg-r select-none" />
          {/* The head sits behind the body so its neck tucks under the collar. */}
          <span className="m-head">
            <img src={lawyerHead} alt="" draggable={false} className="select-none" />
            <img src={lawyerBlink} alt="" draggable={false} className="m-blink select-none" />
          </span>
          <img src={lawyerBody} alt="" draggable={false} className="m-body select-none" />
          <img src={lawyerClean} alt="" draggable={false} className="m-clean select-none" />
          <img src={lawyerArm} alt="" draggable={false} className="m-arm select-none" />
          <img src={lawyerFolder} alt="" draggable={false} className="m-folder select-none" />
        </span>
      </span>
    </span>
  );
}

/** The picture as supplied, unmoving: the panel's greeting. */
export function MascotPortrait({ className }: { className?: string }) {
  return <img src={lawyer} alt="" draggable={false} className={['select-none', className ?? ''].join(' ')} />;
}

/** His face, for the avatar beside answers; `busy` nods while a question is looked up. */
export function MascotFace({ busy = false, className }: { busy?: boolean; className?: string }) {
  return (
    <img
      src={lawyerFace}
      alt=""
      draggable={false}
      className={[busy ? 'm-busy' : '', 'select-none', className ?? ''].join(' ')}
    />
  );
}
